// Engagement detail page — the main operational view.
// Tabs: Overview, Scan, Domains, Findings, Evidence, Retest, Task History, Report.

import { clsx } from "clsx";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "@/components/ui/Badge";
import { Loading } from "@/components/ui/Loading";
import { PageHeader } from "@/components/ui/PageHeader";
import { SelectMenu } from "@/components/ui/SelectMenu";
import { useEngagement, useEngagementFindings } from "@/hooks/useEngagementDetail";
import { getStatusBadgeColors } from "@/types/engagement";
import { useDict, fmt } from "@/i18n/LocaleProvider";
import { EvidenceTab } from "@/components/engagement/EvidenceTab";
import { FindingsTab } from "@/components/engagement/FindingsTab";
import { OverviewTab } from "@/components/engagement/OverviewTab";
import { OwnershipPanel } from "@/components/engagement/OwnershipPanel";
import { ReportTab } from "@/components/engagement/ReportTab";
import { RetestTab } from "@/components/engagement/RetestTab";
import { ScanTab } from "@/components/engagement/ScanTab";
import { TaskHistoryTab } from "@/components/engagement/TaskHistoryTab";

type TabId =
  | "overview"
  | "scan"
  | "domains"
  | "findings"
  | "evidence"
  | "retest"
  | "history"
  | "report";

export function EngagementDetailPage() {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ?? "";

  const d = useDict();
  const t = d.app.engagementDetail;
  const st = d.app.engagements.statuses;
  const engagement = useEngagement(id);
  const findings = useEngagementFindings(id);

  const [tab, setTab] = useState<TabId>("overview");

  if (engagement.isLoading || !engagement.data) {
    return <Loading text={t.loading} />;
  }

  const en = engagement.data;
  const statusBadge = getStatusBadgeColors(en.status);
  const findingCount = findings.data?.length ?? 0;

  const tabs: Array<{ id: TabId; label: string }> = [
    { id: "overview", label: t.tabs.overview },
    { id: "scan", label: en.status === "scanning" ? t.tabs.scanning : t.tabs.scan },
    { id: "domains", label: t.tabs.domains },
    { id: "findings", label: fmt(t.tabs.findings, { n: findingCount }) },
    { id: "evidence", label: t.tabs.evidence },
    { id: "retest", label: t.tabs.retest },
    { id: "history", label: t.tabs.history },
    { id: "report", label: t.tabs.report },
  ];

  return (
    <div>
      <PageHeader
        title={`#${en.display_number ?? "—"} · ${en.name}`}
        subtitle={
          (en.client_name || t.noClient) +
          " · " +
          en.pentest_type +
          " · " +
          t.created +
          " " +
          new Date(en.created_at).toLocaleDateString()
        }
        breadcrumbs={[t.bcEngagements, en.name]}
        actions={
          <Badge color={statusBadge.color} bg={statusBadge.bg}>
            {st[en.status as keyof typeof st] ?? en.status}
          </Badge>
        }
      />

      {/* Tab navigation — branded SelectMenu on mobile (custom dropdown
          with full visual control over both trigger and menu panel),
          tab bar from md+. */}
      <div className="md:hidden mb-4">
        <SelectMenu<TabId>
          value={tab}
          options={tabs.map((t) => ({ value: t.id, label: t.label }))}
          onChange={setTab}
          ariaLabel={t.sectionAria}
        />
      </div>
      <div className="hidden md:flex gap-1 mb-4 border-b border-stroke">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={clsx(
              "px-4 py-[10px] bg-transparent border-none font-mono text-[11px] font-semibold tracking-[0.02em] cursor-pointer transition-all duration-150 whitespace-nowrap shrink-0",
              tab === t.id ? "text-accent" : "text-muted",
            )}
            style={{
              borderBottom:
                tab === t.id
                  ? "2px solid rgb(var(--color-accent))"
                  : "2px solid transparent",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && (
        <OverviewTab
          engagementId={id}
          onGoToScan={() => setTab("scan")}
        />
      )}
      {tab === "scan" && <ScanTab engagement={en} />}
      {tab === "domains" && <OwnershipPanel engagementId={id} />}
      {tab === "findings" && <FindingsTab engagementId={id} />}
      {tab === "evidence" && <EvidenceTab engagementId={id} />}
      {tab === "retest" && <RetestTab engagementId={id} />}
      {tab === "history" && <TaskHistoryTab engagementId={id} />}
      {tab === "report" && <ReportTab engagementId={id} />}
    </div>
  );
}
